import React, { useState, useEffect } from 'react';
import { useAuth } from '../../App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ArrowRight, ArrowLeft, X, Sparkles, Play, CalendarDays, UserCog, CheckCircle2 } from 'lucide-react';

const RELEASE_KEY = 'whats_new_seen_v2026_06_autoshift';

/**
 * "What's New" multi-step tour shown once per device. Walks drivers through
 * the automatic shift tracking release (no more Start/End Shift buttons).
 */
export default function DriverWhatsNewModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  const isDriver = user?.role === 'driver' || user?.role === 'admin-driver';

  useEffect(() => {
    if (!isDriver) return;
    try {
      if (!localStorage.getItem(RELEASE_KEY)) {
        // Tiny delay so the dashboard mounts first and the modal feels intentional.
        const t = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(t);
      }
    } catch (e) { console.warn('WhatsNew localStorage unavailable:', e); }
  }, [isDriver]);

  const dismiss = () => {
    try { localStorage.setItem(RELEASE_KEY, new Date().toISOString()); } catch (e) { console.warn('WhatsNew persist failed:', e); }
    setOpen(false);
  };

  const steps = [
    {
      icon: Play,
      tint: 'from-emerald-500 to-emerald-600',
      title: 'No more Start/End Shift',
      body: 'The Start Shift and End Shift buttons are gone. Ride buttons always work — day or night — with no popup blocking you.',
      illustration: <AutoTrackMock />,
    },
    {
      icon: CalendarDays,
      tint: 'from-sky-500 to-indigo-600',
      title: 'Hours are tracked automatically',
      body: 'Your working hours are now recorded for you: the day starts with your first ride action and ends with your last one. Check the "Attendance" menu to see your month.',
      illustration: <CalendarMock />,
    },
    {
      icon: UserCog,
      tint: 'from-rose-500 to-pink-600',
      title: 'Time off is set by your supervisor',
      body: 'Day Off, Sick Leave and similar statuses are managed by your supervisor. If something looks wrong, just reach out to them and they\'ll fix it.',
      illustration: <SupervisorMock />,
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden gap-0"
        data-testid="whats-new-modal"
      >
        {/* Header gradient */}
        <div className={`relative bg-gradient-to-br ${current.tint} px-5 pt-5 pb-4 text-white`}>
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/20"
            data-testid="whats-new-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 text-xs font-medium opacity-90 mb-1">
            <Sparkles className="h-3.5 w-3.5" /> What's new
          </div>
          <div className="flex items-start gap-3">
            <div className="bg-white/20 backdrop-blur p-2 rounded-md">
              <current.icon className="h-5 w-5" />
            </div>
            <h2 className="text-lg font-bold leading-tight pt-0.5">{current.title}</h2>
          </div>
        </div>

        {/* Illustration */}
        <div className="bg-slate-50 px-5 py-4 border-b border-slate-100 flex items-center justify-center min-h-[150px]">
          {current.illustration}
        </div>

        {/* Body */}
        <div className="px-5 pt-4 pb-3">
          <p className="text-sm text-slate-700 leading-relaxed">{current.body}</p>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 pb-3" data-testid="whats-new-dots">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-slate-800' : 'w-1.5 bg-slate-300'}`}
            />
          ))}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between px-5 pb-5 pt-1 gap-2">
          {step > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStep((s) => s - 1)}
              data-testid="whats-new-back"
              className="text-slate-600"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={dismiss}
              data-testid="whats-new-skip"
              className="text-slate-500"
            >
              Skip
            </Button>
          )}
          {isLast ? (
            <Button
              onClick={dismiss}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="whats-new-finish"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" /> Got it
            </Button>
          ) : (
            <Button
              onClick={() => setStep((s) => s + 1)}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="whats-new-next"
            >
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Illustrations: lightweight UI mocks ---------------- */

function AutoTrackMock() {
  return (
    <div className="w-full max-w-[280px] bg-emerald-50 border border-emerald-300 rounded-md p-3 shadow-sm">
      <div className="flex items-center gap-2">
        <div className="p-1.5 bg-emerald-200 rounded-full">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-900" />
        </div>
        <div className="text-xs font-semibold text-emerald-900">Hours recorded automatically</div>
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] text-emerald-800 bg-white border border-emerald-200 rounded px-2 py-1.5">
        <span>First ride action <b>08:15</b></span>
        <ArrowRight className="h-3 w-3 text-emerald-400" />
        <span>Last ride action <b>17:40</b></span>
      </div>
    </div>
  );
}

function CalendarMock() {
  const days = Array.from({ length: 28 }, (_, i) => i + 1);
  const today = 15;
  const workingSet = new Set([2, 3, 5, 8, 9, 10, 12, 13, 17, 18]);
  const dayOff = new Set([6, 7, 14, 21]);
  const sick = new Set([20]);
  return (
    <div className="bg-white border border-slate-200 rounded-md p-2 w-[260px]">
      <div className="text-[10px] font-semibold text-slate-700 mb-1.5 text-center">June 2026</div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((d) => {
          let cls = 'bg-white text-slate-400 border-slate-100';
          if (workingSet.has(d)) cls = 'bg-emerald-100 text-emerald-800 border-emerald-300';
          if (dayOff.has(d)) cls = 'bg-slate-200 text-slate-700 border-slate-400';
          if (sick.has(d)) cls = 'bg-amber-100 text-amber-800 border-amber-300';
          if (d === today) cls = 'bg-orange-100 text-orange-800 border-orange-400 ring-1 ring-orange-400';
          return (
            <div key={d} className={`h-6 border rounded text-[8px] flex items-center justify-center font-semibold ${cls}`}>
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SupervisorMock() {
  return (
    <div className="flex items-center gap-3">
      <div className="bg-rose-100 border border-rose-300 rounded-lg p-2.5 w-28">
        <div className="text-[9px] font-semibold text-rose-800 flex items-center gap-1">
          <UserCog className="h-3 w-3" /> Supervisor
        </div>
        <div className="mt-1.5 text-[9px] text-slate-600">Sets your time off</div>
      </div>
      <div className="text-slate-400">
        <ArrowRight className="h-4 w-4" />
      </div>
      <div className="bg-slate-100 border border-slate-300 rounded-lg p-2.5 w-24">
        <div className="text-[9px] font-semibold text-slate-700">Day Off</div>
        <div className="mt-1 h-5 w-full bg-slate-300 rounded" />
      </div>
    </div>
  );
}
